import React from 'react';
import { Box, Paper, Tooltip, Typography } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DescriptionIcon from '@mui/icons-material/Description';
import GroupIcon from '@mui/icons-material/Group';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { Metric, PendingMetrics } from '../../utils/invoiceMetrics';
import { formatCurrency } from '../../utils/money';
import { BRAND } from '../../theme';

interface StatCardsProps {
  metrics: PendingMetrics;
}

// These cards count pending invoices only, but they follow every other filter,
// so a broker or date filter moves them along with the table.
const SCOPE = 'Pending invoices matching the filters below.';

const DeltaLine: React.FC<{ metric: Metric }> = ({ metric }) => {
  if (metric.delta === null || metric.delta === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        {metric.delta === 0 ? 'No change vs last 30 days' : 'No comparison available'}
      </Typography>
    );
  }

  const rising = metric.delta > 0;
  const good = rising === metric.riseIsGood;
  const magnitude =
    metric.deltaKind === 'percent'
      ? `${Math.abs(metric.delta).toFixed(1)}%`
      : `${Math.abs(metric.delta)}`;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
      {rising ? (
        <ArrowUpwardIcon sx={{ fontSize: 14, color: good ? BRAND.positive : BRAND.negative }} />
      ) : (
        <ArrowDownwardIcon sx={{ fontSize: 14, color: good ? BRAND.positive : BRAND.negative }} />
      )}
      <Typography
        variant="caption"
        sx={{ color: good ? BRAND.positive : BRAND.negative, fontWeight: 600 }}
      >
        {magnitude}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        vs last 30 days
      </Typography>
    </Box>
  );
};

const StatCard: React.FC<{
  label: string;
  value: string;
  icon: React.ReactNode;
  metric: Metric;
  hint: string;
}> = ({ label, value, icon, metric, hint }) => (
  <Tooltip title={hint}>
  <Paper
    variant="outlined"
    sx={{
      p: 2,
      flex: '1 1 220px',
      minWidth: 0,
      display: 'flex',
      alignItems: 'center',
      gap: 2
    }}
  >
    <Box
      sx={{
        width: 48,
        height: 48,
        flexShrink: 0,
        borderRadius: '50%',
        display: 'grid',
        placeItems: 'center',
        backgroundColor: BRAND.blue,
        color: '#fff'
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 600 }}
      >
        {label}
      </Typography>
      <Typography variant="h5" sx={{ my: 0.25 }} noWrap>
        {value}
      </Typography>
      <DeltaLine metric={metric} />
    </Box>
  </Paper>
  </Tooltip>
);

const StatCards: React.FC<StatCardsProps> = ({ metrics }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2.5 }}>
    <StatCard
      label="Total Pending Amount"
      value={formatCurrency(metrics.totalPendingAmount.value)}
      icon={<AttachMoneyIcon />}
      metric={metrics.totalPendingAmount}
      hint={`${SCOPE} Total charges still awaiting payout.`}
    />
    <StatCard
      label="Pending Invoices"
      value={String(metrics.pendingInvoiceCount.value)}
      icon={<DescriptionIcon />}
      metric={metrics.pendingInvoiceCount}
      hint={`${SCOPE} How many invoices are still pending.`}
    />
    <StatCard
      label="Drivers Awaiting Payout"
      value={String(metrics.driversAwaitingPayout.value)}
      icon={<GroupIcon />}
      metric={metrics.driversAwaitingPayout}
      hint={`${SCOPE} Drivers with at least one pending invoice.`}
    />
    <StatCard
      label="Avg Aging (days)"
      value={String(metrics.averageAgingDays.value)}
      icon={<AccessTimeIcon />}
      metric={metrics.averageAgingDays}
      hint={`${SCOPE} Average days since those invoices were created.`}
    />
  </Box>
);

export default StatCards;
